import { useState } from 'react';

import { CheckIcon, EditIcon, QuestionCircle } from 'fluxo-ui/icons';

import { QUESTION_TYPES } from '../../common/constants';

import type { ComponentEvent } from 'fluxo-ui';

interface QuestionType {
    label: string;
    value: string;
    description: string;
}

interface QuestionTypeSelectorProps {
    value?: string;
    onChange?: (questionType: string) => void;
    disabled?: boolean;
}

const QuestionTypeSelector: React.FC<QuestionTypeSelectorProps> = ({ value, onChange, disabled = false }) => {
    const questionTypes: QuestionType[] = [
        {
            label: 'Single Choice',
            value: QUESTION_TYPES.MCSA,
            description: 'Multiple choice with single answer',
        },
        {
            label: 'Multiple Choice',
            value: QUESTION_TYPES.MCMA,
            description: 'Multiple choice with multiple answers',
        },
        {
            label: 'True/False',
            value: QUESTION_TYPES.TRUE_FALSE,
            description: 'Boolean question',
        },
        {
            label: 'Fill Blanks',
            value: QUESTION_TYPES.FILL_BLANKS,
            description: 'Fill in the missing words',
        },
        {
            label: 'Short Answer',
            value: QUESTION_TYPES.SHORT_ANSWER,
            description: 'Brief text response',
        },
        {
            label: 'Long Answer',
            value: QUESTION_TYPES.LONG_ANSWER,
            description: 'Essay or detailed response',
        },
        {
            label: 'Code',
            value: QUESTION_TYPES.CODE,
            description: 'Programming question',
        },
    ];

    const [selectedType, setSelectedType] = useState(value || QUESTION_TYPES.MCSA);

    const handleChange = (e: ComponentEvent<string>) => {
        setSelectedType(e.value);
        onChange?.(e.value);
    };

    return (
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-slate-900 dark:text-white mb-2">Question Type</label>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">Select the type of question you want to create</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {questionTypes.map((type) => (
                    <button
                        key={type.value}
                        onClick={() => !disabled && handleChange({ value: type.value, name: 'questionType' })}
                        disabled={disabled}
                        className={`p-4 rounded-lg border-2 transition-all text-left ${
                            selectedType === type.value
                                ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-teal-300 dark:hover:border-teal-700'
                        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                        <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <div
                                    className={`p-2 rounded-lg ${
                                        selectedType === type.value ? 'bg-teal-100 dark:bg-teal-900' : 'bg-slate-100 dark:bg-slate-700'
                                    }`}
                                >
                                    <QuestionCircle
                                        className={`w-5 h-5 ${
                                            selectedType === type.value
                                                ? 'text-teal-600 dark:text-teal-400'
                                                : 'text-slate-600 dark:text-slate-400'
                                        }`}
                                    />
                                </div>
                            </div>
                            {selectedType === type.value && <CheckIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" />}
                        </div>
                        <h3
                            className={`font-semibold mb-1 ${
                                selectedType === type.value ? 'text-teal-900 dark:text-teal-100' : 'text-slate-900 dark:text-white'
                            }`}
                        >
                            {type.label}
                        </h3>
                        <p
                            className={`text-sm ${
                                selectedType === type.value ? 'text-teal-700 dark:text-teal-300' : 'text-slate-600 dark:text-slate-400'
                            }`}
                        >
                            {type.description}
                        </p>
                    </button>
                ))}
            </div>

            {selectedType && (
                <div className="mt-4 p-4 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg">
                    <div className="flex items-start gap-3">
                        <EditIcon className="w-5 h-5 text-teal-600 dark:text-teal-400 mt-0.5" />
                        <div>
                            <h4 className="font-medium text-teal-900 dark:text-teal-100 mb-1">
                                {questionTypes.find((t) => t.value === selectedType)?.label} Selected
                            </h4>
                            <p className="text-sm text-teal-700 dark:text-teal-300">
                                {selectedType === QUESTION_TYPES.MCSA || selectedType === QUESTION_TYPES.MCMA
                                    ? 'You can add multiple options with correct answer(s) marked.'
                                    : selectedType === QUESTION_TYPES.TRUE_FALSE
                                      ? 'Students will choose between True or False.'
                                      : selectedType === QUESTION_TYPES.FILL_BLANKS
                                        ? 'Use $answer$ placeholder in your question text for blank spaces.'
                                        : selectedType === QUESTION_TYPES.CODE
                                          ? 'Students will write code in a code editor with syntax highlighting.'
                                          : 'Students will provide a text-based answer.'}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuestionTypeSelector;
